if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
        this.messageElement = this.querySelector('.pickup-availability__message');
        this.isCartItem = this.hasAttribute('cart-item');

        if (!this.hasAttribute('requires-shipping')) {
          this.renderError(true, true);
          this.querySelector('.pickup-availability-preview')?.remove();
          this.querySelector('.delivery-options')?.remove();
        }

        if (!this.hasAttribute('available')) {
          return;
        }
        this.overlay = this.querySelector('.pickup-availability__overlay');
        let latitude = null;
        let longitude = null;
        const _this = this;
        const geoOptions = {
          timeout: 5000
        };

        let geoSuccess = false;

        const geoTimeout = setTimeout(() => {
          if (!geoSuccess) {
            _this.getAvailability(this.dataset.variantId, this.dataset.productId, null, null);
          }
        }, geoOptions.timeout);

        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(geoTimeout);
            geoSuccess = true;
            latitude = position.coords.latitude;
            longitude = position.coords.longitude;
            document.cookie = `latitude=${latitude}; path=/`;
            document.cookie = `longitude=${longitude}; path=/`;
            _this.getAvailability(this.dataset.variantId, this.dataset.productId, latitude, longitude);
          },
          (error) => {
            clearTimeout(geoTimeout);
            _this.getAvailability(this.dataset.variantId, this.dataset.productId, null, null);
          },
          geoOptions
        );
      }

      connectedCallback() {
        this.addEventListener('click', (evt) => {
          if (evt.target.closest('#changeStoreBtn, .change-store, .select-a-store')) {
            evt.preventDefault();
            const drawer = this.renderDrawer();
            setTimeout(() => {
              drawer.show(evt.target);
            }, 20);
            evt.stopPropagation();
          }
        });
      }

      async getAvailability(variantId, productId, latitude, longitude, force = false) {
        if (!variantId || !productId) return;

        if (!force) {
          const urlParams = new URLSearchParams(window.location.search);
          const variantIdFromUrl = urlParams.get('variant');
          if (variantIdFromUrl !== null) {
            variantId = variantIdFromUrl;
          }
        }

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }

        const storefrontAccessToken = this.dataset.accessToken;
        const shopifyStoreUrl = `${rootUrl}api/2024-10/graphql.json`;

        const query = `
        {
          product(id: "gid://shopify/Product/${productId}") {
            variants(first: 200) {
              edges {
                node {
                  id
                  selectedOptions {
                    name
                    value
                  }
                  storeAvailability(first: 200) {
                    edges {
                      node {
                        available
                        pickUpTime
                        location {
                          address {
                            latitude
                            longitude
                            address1
                            address2
                            city
                            province
                            country
                            countryCode
                            zip
                          }
                          id
                          name
                          metafield(namespace: "amb", key: "store_hours") {
                            value
                          }
                        }
                        quantityAvailable
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `;

        const _this = this;
        try {
          const response = await fetch(shopifyStoreUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
            },
            body: JSON.stringify({ query }),
          });

          const data = await response.json();
          const variant = data.data.product.variants.edges.find(edge => edge.node.id === `gid://shopify/ProductVariant/${variantId}`);
          const availability = variant ? variant.node.storeAvailability.edges.filter(store => store.node.location.address.countryCode === this.dataset.countryCode) : [];
          this.variant = variant;
          this.availability = availability;
          if (this.availability.length === 0) {
            this.renderError(true, true);
            return;
          }
          const selectedStoreId = this.dataset.selectedStore;
          let selectedStore = null;
          availability.forEach(store => {
            if (store.node.location.id === `gid://shopify/Location/${selectedStoreId}`) {
              selectedStore = store;
            }
          });
          if (this.isCartItem && selectedStore) {
            const { className, pickupAvailable } = this._getDOMAttributes(selectedStore);
            this.renderStorePickupDetails(selectedStore, pickupAvailable, className);
          } else {
            this.renderAvailability(this.availability, latitude, longitude);
          }
        } catch (e) {
          this.renderError(true, true);
        }
      }

      async renderAvailability(availability, latitude, longitude) {
        let nearestStore = null;
        let storeNameFromCookie = null;
        let storeIdFromCookie = null;
        let allStoresAreUnavailable = true;

        if (!this.customerLoggedIn) {
          const guestStoreInfoCookie = document.cookie.split('; ').find(row => row.startsWith('guestStoreInfo='));
          if (guestStoreInfoCookie) {
            const guestStoreInfo = guestStoreInfoCookie.split('=')[1];
            const storeInfoParts = guestStoreInfo.split(',');
            storeNameFromCookie = storeInfoParts.find(part => part.startsWith('storeName:'))?.split(':')[1];
            storeIdFromCookie = storeInfoParts.find(part => part.startsWith('storeId:'))?.split(':')[1];
          }
        } else {
            const preferredStoreID = await this.fetchPreferredStore();
            if (preferredStoreID) {
              nearestStore = availability.find(store => store.node.location.id.replace('gid://shopify/Location/', '') === preferredStoreID);
            }
        }

        // Get store from cookie if available
        if (storeNameFromCookie && storeIdFromCookie) {
          nearestStore = availability.find(store => store.node.location.id.replace('gid://shopify/Location/', '') === storeIdFromCookie && store.node.location.name === storeNameFromCookie);
        }

        // Get first store if no store is found from cookie and no location is available
        if (!nearestStore) {
          if (!latitude || !longitude) {
            if (availability.length > 0) {
              nearestStore = availability[0];
            } else {
              this.renderError(true, true);
            }
          }
        }

        // Check if all stores are unavailable
        availability.forEach(store => {
          if (store.node.available) {
            allStoresAreUnavailable = false;
          }
        });

        // Get nearest store if location is available
        if (!nearestStore) {
          const toRadians = (degrees) => degrees * (Math.PI / 180);
          const distance = (lat1, lon1, lat2, lon2) => {
            const R = 6371; // Radius of the Earth in km
            const dLat = toRadians(lat2 - lat1);
            const dLon = toRadians(lon2 - lon1);
            const a =
              Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c; // Distance in km
          };

          let minDistance = Infinity;

          availability.forEach(store => {
            const storeLat = store.node.location.address.latitude;
            const storeLon = store.node.location.address.longitude;
            const dist = distance(latitude, longitude, storeLat, storeLon);
            if (dist < minDistance) {
              minDistance = dist;
              nearestStore = store;
            }
          });
        }

        if (nearestStore) {
          this.renderPickupAvailability(nearestStore, allStoresAreUnavailable);
          const expandInfoStoreBtns = document.querySelectorAll('[data-show-more-store-info]');
          expandInfoStoreBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
              btn.classList.toggle('expand');
            })
          });
        } else {
          this.renderError(allStoresAreUnavailable);
        }
      }

      renderPickupAvailability(nearestStore, allStoresAreUnavailable) {
        const { className, pickupAvailable } = this._getDOMAttributes(nearestStore);

        if (this.isCartItem) {
          this.classList.remove('unavailable');
          this.classList.remove('available');
          this.classList.remove('low-stock');
          this.classList.add(className || 'available');
          if (allStoresAreUnavailable) {
            this.classList.add('all-stores-unavailable');
          } else {
            this.classList.add('available');
            this.classList.remove('unavailable');
          }

          this.innerHTML = `${this.messageElement ? this.messageElement.outerHTML : ''}
            <div class="delivery-options">
                <a href="#" class="select-a-store full-unstyled-link">${this.dataset.selectAStore}</a>
            </div>`;
        } else {
          this.renderStorePickupDetails(nearestStore, pickupAvailable, className, allStoresAreUnavailable);
        }
      }

      _getDOMAttributes(nearestStore) {
        const stockThreshold = this.dataset.pickupStockthreshold ? parseInt(this.dataset.pickupStockthreshold, 10) : 5;
        let pickupAvailable = this.dataset.pickupInstock;
        let quantityAvailable = nearestStore?.node?.quantityAvailable || 0;
        let className = "available";
        if (quantityAvailable < stockThreshold && quantityAvailable > 0) {
          pickupAvailable = this.dataset.pickupLowstock;
          className = "low-stock";
        } else if (quantityAvailable === 0) {
          pickupAvailable = this.dataset.pickupOutofstock;
          className = "unavailable";
        }
        return { className, pickupAvailable };
      }

      renderDrawer() {
        const availability = this.availability || [];
        const showStoreHours = this.dataset.pickupShowstorehours;
        const stockThreshold = this.dataset.pickupStockthreshold;
        const variant = this.variant || null;
        const existingDrawer = document.querySelector('pickup-availability-drawer');
        if (existingDrawer) {
          existingDrawer.remove();
        }
        const drawer = document.createElement('pickup-availability-drawer');
        drawer.classList.add('gradient');
        drawer.classList.add('color-scheme-1');
        drawer.setAttribute('tabindex', '-1');
        drawer.setAttribute('role', 'dialog');
        drawer.setAttribute('aria-modal', 'true');
        drawer.setAttribute('aria-labelledby', 'PickupAvailabilityHeading');
        drawer.setAttribute('aria-hidden', 'true');
        const variantOptions = variant?.node?.selectedOptions || [];
        let variantionInformation = '';
          variantOptions.forEach(({ name, value }) => {
            if (name && value) {
              variantionInformation += `${name}: ${value}, `;
            }
          });
          if (variantionInformation.endsWith(', ')) {
            variantionInformation = variantionInformation.slice(0, -2);
          }
        const drawerText = `
            <div class="pickup-availability__inner">
            <div class="pickup-availability-header">
              <div class="d-flex flex-row gap-1">
                <h2 class="h3 pickup-availability-drawer-title" id="PickupAvailabilityHeading">
                  ${this.dataset.productTitle}
                </h2>
                <h4 class="selected-variant-info">
                  ${variantionInformation}
                </h4>
              </div>
              <button class="pickup-availability-drawer-button" type="button" aria-label="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none"
                  viewBox="0 0 18 17">
                    <path
                      d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z"
                      fill="currentColor">
                    </path>
                </svg>
              </button>
            </div>
            <ul class="pickup-availability-list list-unstyled" role="list" data-store-availability-drawer-content="">
              ${availability.map(store => {
                let quantityAvailable = store?.node?.quantityAvailable || 0;
                let className = "available";
                const requestedQuantity = this.dataset.pickupRequestedQty || 1;

                if (quantityAvailable < stockThreshold && quantityAvailable >= requestedQuantity) {
                  className = "low-stock";
                } else if (quantityAvailable === 0) {
                  className = "unavailable";
                }
                return `
                <li class="pickup-availability-list__item"
                  data-store-id="${store.node.location.id.replace('gid://shopify/Location/', '')}">
                  <h3 class="h4 store-name">${store.node.location.name}</h3>
                  ${this.dataset.pickupShowcollectiontime !== 'false' ? `<p class="pickup-availability-preview caption-large"><span class="icon pickup-icon ${className}"></span>
                  ${store.node.pickUpTime}
                  </p>` : '' }
                  <p class="store-hours">${showStoreHours && showStoreHours === 'true' && store.node.location.metafield ? JSON.parse(store.node.location.metafield.value).join('</br>') : ''}</p>
                  <address class="pickup-availability-address">
                    <p>${store.node.location.address.address1}, ${store.node.location.address.address2}</p>
                    <p>${store.node.location.address.city}, ${store.node.location.address.zip}</p>
                    <p>${store.node.location.address.province}, ${store.node.location.address.country} </p>
                  </address>
                  <a class="select-store-button" href="#">${this.dataset.pickupSelectstore}</a>
                </li>
              `
              }).join('')}
            </ul>
            </div>
            <div class="pickup-availability__overlay"></div>
            `;

        drawer.innerHTML = drawerText;
        document.body.appendChild(drawer);
        const selectStoreBtns = drawer.querySelectorAll('.select-store-button');
        selectStoreBtns.forEach((selectStoreBtn) => {
          selectStoreBtn.addEventListener('click', (evt) => {
            evt.preventDefault();
            drawer.hide();
            const listElement = evt.target.closest('li');
            const storeId = listElement.dataset.storeId;
            const store = this.availability.find(store => store.node.location.id.replace('gid://shopify/Location/', '') === storeId);
            let quantityAvailable = store?.node?.quantityAvailable || 0;
            let pickupAvailable = this.dataset.pickupInstock;
            let className = "available";
            const requestedQuantity = this.dataset.pickupRequestedQty || 1;

            if (quantityAvailable < stockThreshold && quantityAvailable >= requestedQuantity) {
              pickupAvailable = this.dataset.pickupLowstock;
              className = "low-stock";
            } else if (quantityAvailable === 0) {
              pickupAvailable = this.dataset.pickupOutofstock;
              className = "unavailable";
            }
            this.renderStorePickupDetails(store, pickupAvailable, className);
            if (this.isCartItem) {
              this._updateSelectedStoreIdAgainstLineItem(store);
            }
            this.dispatchEvent(new CustomEvent('store-selected', {
              detail: {
                store: store.node,
                lineItemId: this.dataset.lineItemId
              }
            }));
            const storeName = store?.node?.location?.name;
            if (!this.customerLoggedIn) {
              document.cookie = `guestStoreInfo=storeName:${storeName},storeId:${storeId}; path=/`;
            } else {
              this.persistPreferredStore(storeId);
            }
          });
        });

        return drawer;
      }

      _updateSelectedStoreIdAgainstLineItem(store) {
        const lineItemId = this.dataset.lineItemId;
        const requestedQuantity = +this.dataset.pickupRequestedQty || 1;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineItemId,  // line item key
            quantity: requestedQuantity,
            properties: {
              selected_store: store.node.location.id.replace('gid://shopify/Location/', ''),
              selected_store_name: store.node.location.name,
              pickup_time: store.node.pickUpTime,
            }
          })
        });
      }

      async persistPreferredStore(storeId) {
        let bodyRequest = {
          store: storeId
        };
        try {
          const response = await fetch('/apps/api/customer/preferred-store', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyRequest)
          });

          if (!response.ok) {
            let errorBody = 'Could not read error response.';
            try { errorBody = await response.text(); } catch { }
            console.error(`Can't persist preferred store. Status: ${response.status}. Body: ${errorBody}`);
          }
        } catch (error) {
          console.error(`Can't persist preferred store.`, error);
        }
      }

      async fetchPreferredStore() {
        try {
          const response = await fetch('/apps/api/customer/preferred-store', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            return data.preferredStore;
          } else {
            console.error(`Failed to fetch preferred store. Status: ${response.status}`);
            return null;
          }
        } catch (error) {
          console.error('Error fetching preferred store:', error);
          return null;
        }
      }

      renderStorePickupDetails(nearestStore,  pickupAvailable, classList, allStoresAreUnavailable) {
        let pickupTime = nearestStore?.node.pickUpTime;
        if (this.dataset.pickupShowcollectiontime === 'false') {
          pickupTime = '';
        }
        const showStoreHours = this.dataset.pickupShowstorehours === 'true';
        const storeHours = showStoreHours && nearestStore?.node?.location?.metafield ? JSON.parse(nearestStore.node.location.metafield.value).join('</br>') : '';
        this.classList.remove('unavailable');
        this.classList.remove('low-stock');
        this.classList.remove('available');
        this.classList.add(classList || 'available');
        this.classList.add('store-selected');
        if (allStoresAreUnavailable) {
          this.classList.add('all-stores-unavailable');
        }
        if (this.isCartItem) {
          this.innerHTML = `${this.messageElement ? this.messageElement.outerHTML : ''}
           <div class="pickup-info">
              <p class="pickup-estimate">${pickupTime}</p>
            </div>`;

          const storeNameElement = this.querySelector('.pickup-availability__message .store_name');
          if (storeNameElement) {
            storeNameElement.innerHTML = nearestStore.node.location.name;
          }
          return;
        }
        // Render the nearest store information
        this.innerHTML = `${this.messageElement ? this.messageElement.outerHTML : ''}
            <div class="delivery-options">
              <div class="store-info">
                  <span class="store-icon"></span>
                  <span class="store-name">${nearestStore.node.location.name}</span>
                  <a href="#" class="change-store">${this.dataset.pickupChangestore}</a>
              </div>

              <div class="pickup-info">
                  <span class="pickup-icon"></span>
                  <div>
                    <span class="pickup-text">${pickupAvailable}</span>
                    <p class="pickup-estimate">${pickupTime}</p>

                    <details class="store-info-collapse">
                        <summary><span class="heading-see-more">see more details +</span>
                          <span class="heading-see-less">see less details -</span></summary>
                        <div class="">
                          <p class="store-hours">${storeHours}</p>
                          <address id="selectedStoreAddress" class="pickup-availability-address">
                            <p>${nearestStore.node.location.address.address1}, ${nearestStore.node.location.address.address2}</p>
                            <p>${nearestStore.node.location.address.city}, ${nearestStore.node.location.address.zip}</p>
                            <p>${nearestStore.node.location.address.province}, ${nearestStore.node.location.address.country} </p>
                          </address>
                        </div>
                    </details>
                  </div>
                  <span class="pickup-details">${this.dataset.pickupCost}</span>
              </div>
            </div>
          `;
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            this.renderError(true, true);
          });
      }

      update(variant) {
        if (variant?.available) {
          let options = variant.options;
          this.dataset.variantOptions = JSON.stringify(options);
          const latitudeCookie = document.cookie.split('; ').find(row => row.startsWith('latitude='));
          const longitudeCookie = document.cookie.split('; ').find(row => row.startsWith('longitude='));
          const latitude = latitudeCookie ? parseFloat(latitudeCookie.split('=')[1]) : null;
          const longitude = longitudeCookie ? parseFloat(longitudeCookie.split('=')[1]) : null;
          if (latitude && longitude) {
            this.getAvailability(variant.id, this.dataset.productId, latitude, longitude, true);
          } else {
            this.getAvailability(variant.id, this.dataset.productId, null, null, true);
          }
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      // parameter: allStoresAreUnavailable
      // If true, the class 'all-stores-unavailable' will be added to the element: this class to hide the button "Select a store" in Cart page
      // parameter: notExistStore
      // If true: case there is no any store, then set class 'unavailable' to the element
      renderError(allStoresAreUnavailable, notExistStore) {
        if (this.messageElement) {
          this.classList.remove('available');
          this.classList.remove('low-stock');
          if (allStoresAreUnavailable) {
            this.classList.add('all-stores-unavailable');
          } else {
            this.classList.add('available');
          }
          if (notExistStore) {
            this.classList.add('unavailable');
          }
        } else {
          this.classList.add('all-stores-unavailable'); // Hide element with class 'all-stores-unavailable'
        }
      }

      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();
        this.onBodyClick = this.handleBodyClick.bind(this);
      }

      connectedCallback() {
        if (this.querySelector('button')) {
         this.querySelector('button').addEventListener('click', () => {
           this.hide();
         });
       }

       this.addEventListener('keyup', (event) => {
         if (event.code.toUpperCase() === 'ESCAPE') this.hide();
       });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer' &&
          target.id != 'changeStoreBtn' || target.closest('.pickup-availability__overlay')
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
